import { ApproversSelector } from './ApproversSelector'
import { CloseIcon } from '../lib/CloseIcon'
import { AVProject } from './types'
import { changeProjectType, changeSupplier } from './util'

type Props = {
  approvers: string[]
  error: string
  onClose: () => void
  onSave: (project: AVProject) => void
  project: AVProject
  setApprovers: (approvers: string[]) => void
  setProject: (project: AVProject) => void
}

type CheckboxFieldName =
  | 'ExpensesReimbursed'
  | 'TravelReimbursed'
  | 'PaidLeave'
  | 'TravelExpensesDeductible'
  | 'ExpensesDeductible'
  | 'TravelDeductible'

function CheckboxField({
  field,
  label,
  project,
  setProject,
}: {
  field: CheckboxFieldName
  label: string
  project: AVProject
  setProject: (project: AVProject) => void
}) {
  return (
    <label className="copra-checkbox-field">
      <input
        type="checkbox"
        checked={Boolean(project[field])}
        onChange={(e) =>
          setProject({
            ...project,
            [field]: e.target.checked,
          })
        }
      />
      <span>{label}</span>
    </label>
  )
}

export function ProjectModal({
  approvers,
  error,
  onClose,
  onSave,
  project,
  setApprovers,
  setProject,
}: Props) {
  const showExpensesControls =
    project.ProjectType === 'Sales' &&
    (project.Supplier === 'ComPas' || project.Supplier === 'MIR')

  return (
    <>
      <div className="cg-modal-background nested"></div>
      <div className="cg-modal-project-dialog">
        <div className="cg-modal-title">
          <div className="flex flex-justify-between flex-align-center">
            <div>{project.ID ? 'Edit project' : 'New project'}</div>
            <div className="cg-close" onClick={onClose}>
              <CloseIcon width={22} height={22} />
            </div>
          </div>
        </div>
        <div className="cg-modal-content">
          {error && <div className="copra-error">{error}</div>}

          <div className="copra-form-row">
            <label className="copra-field copra-field-compact">
              <span>Code</span>
              <input
                value={project.ProjectCode}
                onChange={(e) =>
                  setProject({ ...project, ProjectCode: e.target.value })
                }
              />
            </label>
            <label className="copra-field copra-field-compact">
              <span>Type</span>
              <select
                value={project.ProjectType}
                onChange={(e) => {
                  const nextProjectType = e.target.value as AVProject['ProjectType']
                  if (nextProjectType !== 'Sales') {
                    setApprovers([])
                  }
                  setProject(changeProjectType(project, nextProjectType))
                }}
              >
                <option value="Purchase">Purchase</option>
                <option value="Sales">Sales</option>
                <option value="Kickback">Kickback</option>
              </select>
            </label>
            <label className="copra-field copra-field-compact">
              <span>Magno entity</span>
              <select
                value={project.MagnoEntity}
                onChange={(e) =>
                  setProject({
                    ...project,
                    MagnoEntity: e.target.value as AVProject['MagnoEntity'],
                  })
                }
              >
                <option value="MIR">MIR</option>
                <option value="MIP">MIP</option>
                <option value="MIB">MIB</option>
              </select>
            </label>
            <label className="copra-field copra-field-compact">
              <span>MIP project code</span>
              <input
                value={project.MIPProjectCode || ''}
                onChange={(e) =>
                  setProject({
                    ...project,
                    MIPProjectCode: e.target.value || null,
                  })
                }
              />
            </label>
          </div>

          <div className="copra-form-row">
            <label className="copra-field copra-field-compact">
              <span>Start date</span>
              <input
                type="date"
                value={project.StartDate}
                onChange={(e) =>
                  setProject({ ...project, StartDate: e.target.value })
                }
              />
            </label>
            <label className="copra-field copra-field-compact">
              <span>End date</span>
              <input
                type="date"
                value={project.EndDate || ''}
                onChange={(e) =>
                  setProject({
                    ...project,
                    EndDate: e.target.value || null,
                  })
                }
              />
            </label>
            {project.ProjectType === 'Sales' && (
              <label className="copra-field copra-field-compact">
                <span>Unit type</span>
                <select
                  value={project.UnitType || 'null'}
                  onChange={(e) =>
                    setProject({
                      ...project,
                      UnitType:
                        e.target.value === 'null'
                          ? null
                          : (e.target.value as AVProject['UnitType']),
                    })
                  }
                >
                  <option value="null">Select unit type</option>
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                </select>
              </label>
            )}
            {project.ProjectType === 'Sales' && (
              <label className="copra-field copra-field-compact">
                <span>Cutoff point</span>
                <select
                  value={project.CutoffPoint || 'null'}
                  onChange={(e) =>
                    setProject({
                      ...project,
                      CutoffPoint:
                        e.target.value === 'null'
                          ? null
                          : (e.target.value as AVProject['CutoffPoint']),
                    })
                  }
                >
                  <option value="null">Select cutoff point</option>
                  <option value="LAST_FRIDAY_OF_MONTH">
                    Last Friday of month
                  </option>
                  <option value="LAST_CALENDAR_DAY_OF_MONTH">
                    Last calendar day of month
                  </option>
                </select>
              </label>
            )}
          </div>

          {project.ProjectType === 'Sales' && (
            <div className="copra-form-row">
              <label className="copra-field copra-field-compact">
                <span>Supplier</span>
                <select
                  value={project.Supplier || 'null'}
                  onChange={(e) =>
                    setProject(
                      changeSupplier(
                        project,
                        e.target.value === 'null'
                          ? null
                          : (e.target.value as AVProject['Supplier'])
                      )
                    )
                  }
                >
                  <option value="null">Select supplier</option>
                  <option value="ComPas">ComPas</option>
                  <option value="MIR">MIR</option>
                  <option value="Other">Other</option>
                </select>
              </label>
              <label className="copra-field copra-field-compact">
                <span>Client</span>
                <select
                  value={project.ClientCode || 'null'}
                  onChange={(e) =>
                    setProject({
                      ...project,
                      ClientCode:
                        e.target.value === 'null'
                          ? null
                          : (e.target.value as AVProject['ClientCode']),
                    })
                  }
                >
                  <option value="null">Select client</option>
                  <option value="LG">LG</option>
                  <option value="ING">ING</option>
                  <option value="RELX">RELX</option>
                  <option value="InfoSys">InfoSys</option>
                  <option value="VZ">VZ</option>
                </select>
              </label>
              {project.Supplier === 'Other' && (
                <label className="copra-field copra-field-compact">
                  <span>Exact supplier code</span>
                  <input
                    value={project.ExactSupplierCode || ''}
                    onChange={(e) =>
                      setProject({
                        ...project,
                        ExactSupplierCode: e.target.value || null,
                      })
                    }
                  />
                </label>
              )}
            </div>
          )}

          {showExpensesControls && (
            <>
              <div className="copra-form-row copra-form-row-checkboxes">
                <CheckboxField
                  field="ExpensesReimbursed"
                  label="Expenses reimbursed"
                  project={project}
                  setProject={setProject}
                />
                <CheckboxField
                  field="TravelReimbursed"
                  label="KM reimbursed"
                  project={project}
                  setProject={setProject}
                />
                <CheckboxField
                  field="PaidLeave"
                  label="Paid leave"
                  project={project}
                  setProject={setProject}
                />
              </div>
              <div className="copra-form-row copra-form-row-checkboxes">
                <CheckboxField
                  field="TravelExpensesDeductible"
                  label="Travel exp. deductible"
                  project={project}
                  setProject={setProject}
                />
                <CheckboxField
                  field="ExpensesDeductible"
                  label="Other exp. deductible"
                  project={project}
                  setProject={setProject}
                />
                <CheckboxField
                  field="TravelDeductible"
                  label="KM deductible"
                  project={project}
                  setProject={setProject}
                />
                <label className="copra-inline-field">
                  <span>Max deductible travel</span>
                  <input
                    type="number"
                    value={project.MaxDeductibleTravel ?? ''}
                    onChange={(e) =>
                      setProject({
                        ...project,
                        MaxDeductibleTravel:
                          e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                  />
                </label>
              </div>
            </>
          )}

          {project.ProjectType === 'Sales' && (
            <div className="copra-approvers">
              <ApproversSelector
                approvers={approvers}
                setApprovers={(nextApprovers: string[]) =>
                  setApprovers(nextApprovers)
                }
              />
            </div>
          )}

          <div className="cg-modal-actions">
            <button className="cg-modal-button secondary" onClick={onClose}>
              Cancel
            </button>
            <button className="cg-modal-button" onClick={() => onSave(project)}>
              Save
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
